import React, { FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CloudDownload as CloudDownloadIcon,
  FileCopy as FileCopyIcon,
  HelpOutline as HelpOutlineIcon,
} from '@material-ui/icons';
import { DocumentNode, OperationVariables, useApolloClient } from '@apollo/client';
import { useToaster } from '@/components/core/Toaster';
import { Scope } from '@/contexts/ScopeContext';
import {
  DATA_EXPORT_BATCH_DOWNLOAD_URL,
  DataExportBatchDownloadUrlData,
  DataExportBatchDownloadUrlVars,
  DataExportBatchStatus,
  DataExportDeliveryChannel,
  DataExportDeliveryStatus,
  DataExportRunStatus,
  RedeliverResult,
} from '@/graph/data-export-admin';
import { BackfillInvokeResult } from '@/graph/group-performance';
import { openBlankTab } from '@/util/openBlankTab';
import { tryParseJson2 } from '@/util/parseJson';

/**
 * Shared building blocks for the data-export admin page.
 *
 * Anything used by more than one tab lives here: styles, run/delivery
 * status chips, the SectionShell wrapper, the result-display box, the
 * download button (which fetches a presigned URL lazily on click), and
 * the `useInvoker` hook every mutation panel uses.
 *
 * Tab-specific pieces (the trigger panel, the channels list, the
 * attach/detach UI) stay in their own files to keep this one focused on
 * primitives.
 */

export const HISTORY_PAGE_SIZE = 10;
export type TabKey = 'manual' | 'scheduled';
export type Severity = 'success' | 'error';

export interface RunResult {
  severity: Severity;
  runId: string | null;
  message: string;
  dryRun: boolean;
}

export const useSharedStyles = makeStyles(theme => ({
  // `padding: theme.spacing(3)` (24px) matches the visible gutter on
  // the Patients and Analytics list pages — those go through richer
  // route shells that contribute their own padding, so a plain
  // `spacing(2)` here would leave content sitting closer to the
  // sidebar edge than the operator expects from those siblings.
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
  },
  // Tabs render naturally (no Paper card around them) to match the
  // analytics + scheduled-tasks pages. The bottom border of the
  // MUI Tabs component already provides the divider line under the
  // active-tab indicator; wrapping in a Paper would add a redundant
  // elevated card that looked out-of-place vs. those siblings.
  tabs: {
    marginBottom: theme.spacing(2),
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  // Section renders directly on the page background (no Paper card),
  // so it does not add its own padding — the page-level root padding
  // already insets it from the edges. Internal vertical rhythm comes
  // from the gap between title, divider, and children.
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  sectionDescription: {
    marginTop: theme.spacing(-1),
  },
  sectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  helpIcon: {
    color: theme.palette.text.secondary,
  },
  controlsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  // Vertical variant of `controlsRow` — used when the trigger form is
  // docked into the left column of the manual tab's split layout. Fields
  // stack and stretch to the column width instead of flowing in a wrapping
  // row.
  controlsColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
  },
  // Overrides the fixed/min widths the fields carry for the horizontal
  // row layout so they fill the narrow form column in vertical mode.
  fieldFull: {
    width: '100%',
    minWidth: 0,
  },
  // Manual tab two-column split: a fixed-ish form column on the left and a
  // wider, flexible history-table column on the right. Collapses to a
  // single stacked column on small screens.
  manualSplit: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  manualFormCol: {
    flex: '0 0 340px',
    maxWidth: 340,
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      maxWidth: '100%',
      width: '100%',
    },
  },
  manualTableCol: {
    // `minWidth: 0` lets the table column shrink within the flex row so a
    // wide table scrolls inside its own container instead of pushing the
    // form column narrower.
    flex: '1 1 0',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  fieldExport: {
    minWidth: 280,
  },
  fieldDateRange: {
    minWidth: 240,
  },
  fieldName: {
    minWidth: 240,
  },
  fieldType: {
    minWidth: 140,
  },
  fieldConfig: {
    flex: 1,
    minWidth: 320,
  },
  fieldChannelMulti: {
    minWidth: 320,
    flex: 1,
  },
  buttonProgress: {
    marginRight: theme.spacing(1),
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  resultBox: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    borderLeft: `4px solid ${theme.palette.divider}`,
  },
  resultBoxSuccess: {
    borderLeftColor: theme.palette.success.main,
  },
  resultBoxError: {
    borderLeftColor: theme.palette.error.main,
  },
  runIdRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  runIdMono: {
    fontFamily: 'monospace',
  },
  message: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  emptyBanner: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  failureBanner: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  failureBannerIcon: {
    color: theme.palette.error.dark,
    marginTop: 2,
  },
  failureBannerTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    maxWidth: '100%',
  },
  // Column-header cells: muted, medium-weight, with a slightly stronger
  // bottom rule than the default row divider so the header reads as a
  // distinct band rather than just another row.
  headCell: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  // Trailing action-icon cell — kept on one line and nudged off the
  // right edge so the icons form a clean right-aligned cluster.
  cellActions: {
    whiteSpace: 'nowrap',
  },
  tableEmpty: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  statusChip: {
    fontWeight: 500,
  },
  statusSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  statusFailed: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  statusRunning: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
  statusNeutral: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[800],
  },
  runIdCell: {
    fontFamily: 'monospace',
    fontSize: '0.85em',
  },
  cellNowrap: {
    whiteSpace: 'nowrap',
  },
  cellTimestamp: {
    whiteSpace: 'nowrap',
    fontSize: '0.85em',
    color: theme.palette.text.secondary,
  },
  channelChip: {
    margin: theme.spacing(0.25),
  },
  configMono: {
    fontFamily: 'monospace',
    fontSize: '0.85em',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  deliverySubTable: {
    backgroundColor: theme.palette.background.default,
  },
  // Collapse content sits in its own TableRow > TableCell wrapper. The
  // default cell padding leaves a visible gap above + below the
  // expansion content; zeroing it lets the expansion sit flush against
  // the parent row.
  expansionCell: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  // Warning icon sits next to a status chip in the Scheduled-tab last-
  // run column. A small nudge keeps it from kissing the chip.
  inlineWarningIcon: {
    marginLeft: theme.spacing(0.5),
  },
  // "No channels" placeholder in the attach/detach chip row needs to
  // separate from the trailing add-picker.
  noChannelsCaption: {
    marginRight: theme.spacing(1),
  },
  // The inline channel-attach picker on the Scheduled tab is wider than
  // the default Select to leave room for channel names.
  attachPickerField: {
    minWidth: 200,
  },
  // Per-delivery error label inside the expansion table — small gap
  // after the status chip.
  deliveryErrorLabel: {
    marginLeft: theme.spacing(1),
  },
  // Reserved-space hide. Used on the Dry-run checkbox in advanced
  // mode so toggling Shift+D does not shift the action row height
  // (the FormControlLabel is taller than a small Button by ~6–10px,
  // which propagates down through the section and visibly bumps the
  // history table below it). `visibility: hidden` keeps the element
  // in the layout flow but removes paint + pointer events; the
  // explicit `pointer-events: none` is belt-and-braces for browsers
  // that handle the `inert`-ish semantics inconsistently.
  hiddenReserved: {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
  // The channels dialog body holds a small table; keep it from
  // collapsing flush against the form above.
  dialogSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  // Inside each dialog sub-section (Add a channel, Active channels),
  // children stack with their own gap so subtitle / description / form
  // controls / action button don't collapse against each other.
  dialogFormStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  // Tight inner stack — used for paired content where a tighter rhythm
  // is wanted (e.g. controls row + its helper caption).
  dialogFormStackTight: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

/* ---------- formatters ---------- */

/**
 * Render an ISO timestamp in a specified IANA timezone (e.g.
 * `America/Los_Angeles`). Every timestamp on the Data Export page is
 * an audit / runbook record — operators reason about WHEN something
 * fired in the org's local time, not in the browser's local time or
 * the server's outbound offset. Falls back to the raw string when
 * `Date` parsing fails so a malformed value is at least diagnosable.
 *
 * Output is ISO-style `YYYY-MM-DD HH:mm:ss` so the date portion matches
 * the `YYYY-MM-DD` range strings rendered elsewhere in the tables (built
 * from `formatWindow`). `hourCycle: 'h23'` keeps midnight as `00:00:00`
 * rather than the `24:00:00` some engines emit.
 */
export const formatTimestamp = (iso: string | null, timeZone: string): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find(p => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}:${part('second')}`;
};

/**
 * Bind `formatTimestamp` to the current org's IANA timezone (read
 * from the Scope context — same field other pages use, e.g.
 * `scope.hospital.timezone.id`). UTC is the fallback when the scope
 * hasn't loaded yet OR an org has no timezone configured; both are
 * edge cases that should not happen on the Data Export page (which
 * only renders after `definitions` finishes loading from a scoped
 * org), but the fallback keeps the formatter total.
 */
export const useTimestampFormatter = (): ((iso: string | null) => string) => {
  const scope = useContext(Scope);
  const timeZone: string = scope?.hospital?.timezone?.id ?? 'UTC';
  return useMemo(() => (iso: string | null) => formatTimestamp(iso, timeZone), [timeZone]);
};

export const formatWindow = (start: string, end: string): string => (start === end ? start : `${start} → ${end}`);

/** `HH:00` label for an org-local firing hour. */
export const formatHour = (h: number): string => `${h.toString().padStart(2, '0')}:00`;

// Operator-facing label is the shape's name only. `shapeId` references
// the worker-side implementation (e.g. "staff-attribution") and is
// not meaningful to the operator — multiple DB shapes can share the
// same implementation, and the distinguishing identity is the name.
export const exportLabel = (name: string): string => name;

export const definitionKey = (def: { shapeId: string; name: string }) => `${def.shapeId}||${def.name}`;

const parseChannelJson = (json: string): Record<string, unknown> | null => {
  const parsed = tryParseJson2(json, null);
  return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
};

const formatChannelLocation = (type: string, config: Record<string, unknown>, fallback: string): string => {
  if (type === 's3' && typeof config.bucket === 'string') return `s3:${config.bucket}`;
  if (type === 'local' && typeof config.directory === 'string') return `local:${config.directory}`;
  return fallback;
};

/**
 * Pretty-print a delivery `channelSnapshotJson` for table rows. Returns
 * `s3:<bucket>` / `local:<directory>` for known shapes, falls back to
 * the raw JSON for anything else (forward-compat with future channel
 * types). Bad JSON renders as `(unparseable)` — visible signal rather
 * than a silent crash.
 */
export const formatChannelSnapshot = (snapshotJson: string): string => {
  const obj = parseChannelJson(snapshotJson);
  if (!obj) return '(unparseable)';
  const type = typeof obj.type === 'string' ? obj.type : 'unknown';
  return formatChannelLocation(type, obj, `${type}:${snapshotJson}`);
};

/**
 * Pretty-print a `~~export_delivery_channel.configJson` for the channels
 * list. Mirrors `formatChannelSnapshot` but takes the channel's
 * standalone config (no `type` discriminator inside; that lives on the
 * sibling column).
 */
export const formatChannelConfig = (type: string, configJson: string): string => {
  const obj = parseChannelJson(configJson);
  if (!obj) return '(unparseable)';
  return formatChannelLocation(type, obj, configJson);
};

/* ---------- status chips ---------- */

type StatusClassKey = 'statusSuccess' | 'statusFailed' | 'statusRunning';

// Statuses missing from the map (e.g. `pending`) render neutral. The
// API collapses internal batch stages (`merging`, `partial`) before
// they reach the FE, so only presented values appear here.
const STATUS_CLASS_KEYS: Record<string, StatusClassKey> = {
  success: 'statusSuccess',
  failed: 'statusFailed',
  running: 'statusRunning',
  producing: 'statusRunning',
  delivering: 'statusRunning',
};

const StatusChip: FC<{ status: string }> = ({ status }) => {
  const classes = useSharedStyles();
  const className = classes[STATUS_CLASS_KEYS[status] ?? 'statusNeutral'];
  return <Chip size="small" label={status} className={`${classes.statusChip} ${className}`} />;
};

export const RunStatusChip: FC<{ status: DataExportRunStatus }> = ({ status }) => <StatusChip status={status} />;

export const BatchStatusChip: FC<{ status: DataExportBatchStatus }> = ({ status }) => <StatusChip status={status} />;

export const DeliveryStatusChip: FC<{ status: DataExportDeliveryStatus }> = ({ status }) => (
  <StatusChip status={status} />
);

/* ---------- channel multi-select ---------- */

export const ChannelMultiSelect: FC<{
  channels: DataExportDeliveryChannel[];
  value: number[];
  onChange: (next: number[]) => void;
  label: string;
  /** `renderValue` placeholder shown when nothing is selected. */
  emptyHint: ReactNode;
  /** Menu copy shown when the org has no active channels. */
  emptyListMessage: string;
  disabled?: boolean;
  className?: string;
  fullWidth?: boolean;
  displayEmpty?: boolean;
}> = ({
  channels,
  value,
  onChange,
  label,
  emptyHint,
  emptyListMessage,
  disabled,
  className,
  fullWidth,
  displayEmpty,
}) => {
  const classes = useSharedStyles();
  const byId = useMemo(() => {
    const m = new Map<number, DataExportDeliveryChannel>();
    for (const c of channels) m.set(c.id, c);
    return m;
  }, [channels]);
  return (
    <FormControl size="small" variant="outlined" className={className} fullWidth={fullWidth} disabled={disabled}>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={e => onChange((e.target.value as number[]) ?? [])}
        input={<Input />}
        label={label}
        disabled={disabled || channels.length === 0}
        displayEmpty={displayEmpty}
        renderValue={selected => {
          const ids = selected as number[];
          if (ids.length === 0) return emptyHint;
          return (
            <Box display="flex" flexWrap="wrap">
              {ids.map(id => {
                const ch = byId.get(id);
                return (
                  <Chip
                    key={id}
                    size="small"
                    className={classes.channelChip}
                    // Operator-facing name only. The transport-level
                    // type (s3/local) is an implementation detail —
                    // an `s3` channel can be fronted by AWS Transfer
                    // Family as SFTP, so the `type` discriminator is
                    // about the worker's delivery path, not what the
                    // customer sees on the receiving end. Exposing it
                    // here would confuse operators who think in
                    // terms of "the SFTP channel for customer X".
                    label={ch ? ch.name : `#${id}`}
                  />
                );
              })}
            </Box>
          );
        }}
      >
        {channels.length === 0 ? (
          <MenuItem disabled value="">
            {emptyListMessage}
          </MenuItem>
        ) : (
          channels.map(c => (
            <MenuItem key={c.id} value={c.id}>
              <Checkbox checked={value.indexOf(c.id) > -1} />
              <ListItemText primary={c.name} />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

/* ---------- copyable id chip ---------- */

export const CopyableRunId: FC<{ value: number | string; label?: string }> = ({ value, label = 'Copy run id' }) => {
  const classes = useSharedStyles();
  const toaster = useToaster();
  const onCopy = () => {
    navigator.clipboard?.writeText(String(value)).then(
      () => toaster.success('Run ID copied'),
      () => toaster.error('Copy failed')
    );
  };
  return (
    <span className={classes.runIdCell}>
      {value}
      <Tooltip title={label}>
        <IconButton size="small" onClick={onCopy} aria-label="copy-run-id">
          <FileCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </span>
  );
};

/* ---------- download buttons ---------- */

const POPUP_BLOCKED_MESSAGE = 'Your browser blocked the download popup. Allow popups for this site and try again.';

type TicketedDownloadOptions = {
  /** Resolve the destination URL; resolve to `null` when nothing is downloadable. */
  resolveUrl: () => Promise<string | null>;
  /** Prefix for resolution failures, e.g. "Could not start download". */
  errorPrefix: string;
  /** Toast shown when `resolveUrl` resolves to `null`. */
  noUrlMessage: string;
};

/**
 * Popup-safe lazy-download driver shared by the batch + run download
 * buttons. Owns the tab lifecycle: the blank tab MUST be opened
 * synchronously in the click handler before any await — `window.open`
 * after the async round-trip happens outside the user-gesture window
 * and browsers block it as a programmatic popup (see the `openBlankTab`
 * docstring). Once `resolveUrl` comes back the tab is navigated via
 * `location.replace`, or closed with an error toast when resolution
 * fails.
 */
function useTicketedDownload({ resolveUrl, errorPrefix, noUrlMessage }: TicketedDownloadOptions) {
  const toaster = useToaster();
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    // MUST run synchronously before any await — see openBlankTab docstring.
    const tab = openBlankTab();
    if (!tab) {
      toaster.error(POPUP_BLOCKED_MESSAGE);
      return;
    }
    setLoading(true);
    const finishWith = (message: string) => {
      if (!tab.closed) tab.close();
      toaster.error(message);
    };
    try {
      const url = await resolveUrl();
      if (!url) {
        finishWith(noUrlMessage);
        return;
      }
      if (tab.closed) return;
      tab.location.replace(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      finishWith(`${errorPrefix}: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  return { loading, onClick };
}

const DownloadIconButton: FC<{
  tooltip: string;
  disabled: boolean;
  loading: boolean;
  onClick: () => void;
  ariaLabel: string;
}> = ({ tooltip, disabled, loading, onClick, ariaLabel }) => (
  <Tooltip title={tooltip}>
    {/* span wrapper required so the Tooltip can attach to a disabled button */}
    <span>
      <IconButton size="small" onClick={onClick} disabled={disabled || loading} aria-label={ariaLabel}>
        {loading ? <CircularProgress size={14} /> : <CloudDownloadIcon fontSize="inherit" />}
      </IconButton>
    </span>
  </Tooltip>
);

/**
 * Per-batch download of the MERGED artifact. A click lazily queries
 * `dataExportBatchDownloadUrl` (network-only — the page never caches
 * presigned URLs across clicks; they expire in 60s) and navigates the
 * pre-opened tab to the presigned S3 GET URL. Tab navigation is used
 * instead of an `<a download>` link because the presigned URL embeds
 * Content-Disposition from S3, so the browser-side `download` attribute
 * would be ignored anyway.
 *
 * The merge model guarantees exactly one file per batch — no zip
 * bundling, no streaming proxy, no download ticket.
 */
export const BatchDownloadButton: FC<{
  organizationId: number;
  batchId: number;
  /** Gate on `batch.artifactLocation != null` — merge succeeded. */
  enabled: boolean;
  tooltip: string;
}> = ({ organizationId, batchId, enabled, tooltip }) => {
  const client = useApolloClient();
  const { loading, onClick } = useTicketedDownload({
    resolveUrl: async () => {
      const { data } = await client.query<DataExportBatchDownloadUrlData, DataExportBatchDownloadUrlVars>({
        query: DATA_EXPORT_BATCH_DOWNLOAD_URL,
        variables: { organizationId, batchId },
        fetchPolicy: 'network-only',
      });
      return data?.dataExportBatchDownloadUrl ?? null;
    },
    errorPrefix: 'Could not get download URL',
    noUrlMessage: 'No merged artifact available for this batch',
  });
  return (
    <DownloadIconButton
      tooltip={tooltip}
      disabled={!enabled}
      loading={loading}
      onClick={onClick}
      ariaLabel="download-batch"
    />
  );
};

/* ---------- result display ---------- */

export const ResultDisplay: FC<{ result: RunResult | null }> = ({ result }) => {
  const classes = useSharedStyles();
  const toaster = useToaster();
  if (!result) return null;
  const copyRunId = () => {
    if (!result.runId) return;
    navigator.clipboard?.writeText(result.runId).then(
      () => toaster.success('Run ID copied'),
      () => toaster.error('Copy failed')
    );
  };
  return (
    <Box
      className={`${classes.resultBox} ${
        result.severity === 'success' ? classes.resultBoxSuccess : classes.resultBoxError
      }`}
    >
      {result.runId && (
        <div className={classes.runIdRow}>
          <Typography variant="caption" color="textSecondary">
            Run ID
          </Typography>
          <Typography variant="body2" className={classes.runIdMono}>
            {result.runId}
          </Typography>
          <Tooltip title="Copy Run ID">
            <IconButton size="small" onClick={copyRunId} aria-label="copy-run-id">
              <FileCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          {result.dryRun && (
            <Typography variant="caption" color="textSecondary">
              (dry run)
            </Typography>
          )}
        </div>
      )}
      <Typography variant="body2" className={classes.message}>
        {result.message || (result.severity === 'success' ? 'OK' : 'Unknown error')}
      </Typography>
    </Box>
  );
};

/* ---------- section shell ---------- */

/**
 * Section frame for content groups. Renders title + optional help +
 * description + a divider, then children — directly on the page
 * background, not inside an elevated Paper card. Matches the section
 * treatment on the Patients and Analytics admin pages (sections are
 * delineated by spacing + a horizontal divider under the title, not
 * by an elevated card).
 */
export const SectionShell: FC<{
  title: string;
  description?: ReactNode;
  help?: string;
  children: ReactNode;
}> = ({ title, description, help, children }) => {
  const classes = useSharedStyles();
  return (
    <Box className={classes.section}>
      <div className={classes.sectionTitleRow}>
        <Typography variant="h6">{title}</Typography>
        {help && (
          <Tooltip title={help} arrow placement="right">
            <HelpOutlineIcon fontSize="small" className={classes.helpIcon} />
          </Tooltip>
        )}
      </div>
      {description && (
        <Typography variant="body2" color="textSecondary" className={classes.sectionDescription}>
          {description}
        </Typography>
      )}
      <Divider />
      {children}
    </Box>
  );
};

/* ---------- dry-run toggle ---------- */

export const DryRunControl: FC<{
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => (
  <FormControlLabel
    className={className}
    control={<Checkbox checked={value} onChange={e => onChange(e.target.checked)} size="small" disabled={disabled} />}
    label={<Typography variant="body2">Dry run</Typography>}
  />
);

/* ---------- invoke button ---------- */

export const InvokeButton: FC<{
  label: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}> = ({ label, loading, disabled, onClick }) => {
  const classes = useSharedStyles();
  return (
    <Button variant="contained" color="primary" onClick={onClick} disabled={loading || disabled}>
      {loading ? <CircularProgress size={16} className={classes.buttonProgress} /> : null}
      {label}
    </Button>
  );
};

/* ---------- useInvoker ---------- */

/**
 * Normalized mutation outcome consumed by `useInvoker`. Mutation result
 * envelopes differ per root field (`BackfillInvokeResult` carries a
 * `success` flag + coordinator runId; `RedeliverResult` has neither —
 * its failures arrive as GraphQL errors), so each call site maps its
 * payload through one of the adapters below instead of the hook
 * hardcoding a single envelope shape.
 */
export type InvokeOutcome = {
  severity: Severity;
  runId: string | null;
  message: string;
};

/** Adapt a `BackfillInvokeResult` envelope (trigger / retry mutations). */
export const backfillOutcome = (r: BackfillInvokeResult | null | undefined): InvokeOutcome | undefined =>
  r ? { severity: r.success ? 'success' : 'error', runId: r.runId ?? null, message: r.message } : undefined;

/**
 * Adapt a `RedeliverResult` envelope. A resolved mutation means the
 * redelivery rows were inserted and dispatched — failures arrive as
 * GraphQL errors and surface via the hook's catch path — so severity is
 * always success here; voided channels the server skipped are appended
 * to the message so the operator sees them without expanding anything.
 */
export const redeliverOutcome = (r: RedeliverResult | null | undefined): InvokeOutcome | undefined =>
  r
    ? {
        severity: 'success',
        runId: null,
        message:
          r.skippedChannels.length > 0
            ? `${r.message} Skipped voided channels: ${r.skippedChannels.join(', ')}.`
            : r.message,
      }
    : undefined;

/**
 * Shared mutation-driver hook. Each trigger panel (manual, redeliver,
 * retry) calls `invoke(variables, dryRun)`, gets back loading + result
 * state, and surfaces a toaster notice. The `extractResult` function
 * lets one hook drive every mutation regardless of root field name or
 * envelope shape — pass `d => backfillOutcome(d?.someMutationName)` (or
 * the `redeliverOutcome` adapter) and you're done.
 *
 * `onSettled` fires after every outcome, not just success: a failed
 * trigger/redeliver has usually still mutated server state (batch
 * created and then failed, runs released, deliveries re-claimed), so
 * dependent lists must refetch either way.
 */
export function useInvoker<TData, TVars extends OperationVariables>(opts: {
  mutation: DocumentNode;
  extractResult: (data: TData | null | undefined) => InvokeOutcome | undefined;
  label: string;
  onSettled?: () => void;
}) {
  const { mutation, extractResult, label, onSettled } = opts;
  const client = useApolloClient();
  const toaster = useToaster();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);

  const invoke = useCallback(
    async (variables: TVars, dryRun: boolean) => {
      setLoading(true);
      try {
        const { data } = await client.mutate<TData, TVars>({ mutation, variables });
        const r = extractResult(data);
        const next: RunResult = {
          severity: r?.severity ?? 'error',
          runId: r?.runId ?? null,
          message: r?.message ?? 'No response from server',
          dryRun,
        };
        setResult(next);
        if (next.severity === 'success') {
          toaster.success(`${label} invoked${dryRun ? ' (dry run)' : ''}`);
        } else {
          toaster.error(`${label} failed: ${next.message}`);
        }
        onSettled?.();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Network error';
        setResult({ severity: 'error', runId: null, message, dryRun });
        toaster.error(`${label} invocation failed: ${message}`);
        onSettled?.();
      } finally {
        setLoading(false);
      }
    },
    [client, mutation, extractResult, toaster, label, onSettled]
  );

  return { loading, result, invoke };
}
